/**
 * tm/pages/ClientDeskPage.tsx — 거래처 기한 (기한 알림 1단계 G · 2026-10-24)
 *  fphub 「거래처 관리」의 로움 쪽 모습 — 팀장님들이 보는 기한 · 보낸 처리 요청 · 이어진 팀장님 · 규칙표 · 아침 문자
 *  처리 요청은 이미 정식 업무 + 오재인 일일 업무로 들어와 있다 — 여기서는 한눈에 보고 업체로 건너간다
 *  바꾸는 것(아침 문자 받는 사람 · 규칙표 · 잇기 풀기 · 세무사 표시)은 지도사만(서버가 막음 · 화면은 can_edit 로 숨김)
 */
import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { req } from '../api';
import { C, T } from '../components/ui';
import { useWindowWidth } from '../../shared';
import ClientAsksPanel from '../components/ClientAsksPanel';
import { matchFieldsFromCompany, matchProgress } from '../components/CompanyMatchFields';

interface DL { id: string; company_id: string; company_name: string; rule_key: string; title: string; due_date: string; dday: number | null; source: string; status: string; fps: string[] }
interface RQ { id: string; company_id: string; company_name: string; kind: string; kind_label: string; subject: string; memo: string; created_at: string; fp: string; task_status: string; task_type: string }
interface FP { id: string; name: string; title: string; company: string; tail: string; role: string; linked_at: string; active: boolean; clients: number; morning_off?: boolean }
interface Rule { key: string; grp: string; name: string; base_kind: string; period: number | null; unit: string | null; alert_days: number[]; active: boolean; reviewed_by: string; reviewed_at: string | null }
interface Day { day: string; sent: number; empty: number; failed: number; skipped: number; held?: number; mode: string }
/** 팀장님 문답(자체 제품 · 직접 생산 · 매장) 뒤 채울 업체(2026-10-26 · services/clientFit) */
type Ans = 'yes' | 'no' | 'unknown' | '';
interface Fit { company_id: string; company_name: string; fp_name: string; own_product: Ans; makes: Ans; store: Ans; answered_at: string; conflict: boolean; company: Parameters<typeof matchFieldsFromCompany>[0] }
interface Desk { today: string; can_edit: boolean; deadlines: DL[]; requests: RQ[]; fps: FP[]; rules: Rule[]; morning: { mode: 'boss' | 'fp' | 'off'; days: Day[] }; fits?: Fit[] }
type Tab = 'deadlines' | 'fits' | 'requests' | 'asks' | 'fps' | 'rules';

const NAVY = '#1B3A5C';
const MUTED = '#6B7280';   // 흰 바탕 4.8:1 — C.muted(#9CA3AF)는 대비가 모자라 이 화면에서는 안 씀
const ell: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };
const card: CSSProperties = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' };
const WD = '일월화수목금토';
const YEAR = String(new Date().getFullYear());
/** 날짜 — 올해가 아니면 연도까지(2027/1/12(화)) */
const md = (d: string | null) => (d && d.length >= 10 ? `${d.slice(0, 4) === YEAR ? '' : `${d.slice(0, 4)}/`}${+d.slice(5, 7)}/${+d.slice(8, 10)}(${WD.charAt(new Date(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10)).getDay())})` : '');
const ddayTxt = (n: number | null) => (n == null ? '' : n === 0 ? '오늘' : n > 0 ? `D-${n}` : `D+${-n}`);
const ddayTone = (n: number | null): [string, string] => (n == null ? ['#F3F4F6', C.sub] : n <= 7 ? ['#FDECEC', '#B42318'] : n <= 30 ? ['#FEF3C7', '#92400E'] : ['#F3F4F6', '#4B5563']);
const SRC: Record<string, string> = { lookup: '중소벤처24', doc: '문서', direct: '직접' };
const MODE: Record<Desk['morning']['mode'], [string, string]> = {
  boss: ['지도사 미리 보기', '팀장님께 갈 문자를 지도사 휴대폰으로만 보내요(하루 5통까지). 첫 2주 동안 문구를 확인하는 단계예요.'],
  fp: ['팀장님께', '이어진 팀장님 휴대폰으로 하루 한 번 보내요.'],
  off: ['보내지 않음', '아침 문자를 보내지 않아요.'],
};

const btn = (kind: 'pri' | 'line' | 'quiet' = 'line', off = false): CSSProperties => (off ? {
  height: 32, padding: '0 12px', borderRadius: 8, fontSize: T.md, fontWeight: 700, cursor: 'not-allowed', whiteSpace: 'nowrap', fontFamily: 'inherit',
  border: '1px solid #E5E7EB', background: '#F3F4F6', color: '#9CA3AF',
} : {
  height: 32, padding: '0 12px', borderRadius: 8, fontSize: T.md, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
  border: kind === 'pri' ? `1px solid ${NAVY}` : kind === 'line' ? `1px solid ${C.borderDk}` : 'none',
  background: kind === 'pri' ? NAVY : kind === 'line' ? '#fff' : 'transparent', color: kind === 'pri' ? '#fff' : kind === 'line' ? C.text : C.sub,
});

export default function ClientDeskPage({ onOpenCompany, onToast }: { onOpenCompany: (companyId: string) => void; onToast: (msg: string, kind?: 'success' | 'error') => void }) {
  const w = useWindowWidth();
  const narrow = w < 760;
  const [desk, setDesk] = useState<Desk | null>(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<Tab>('deadlines');
  const [busy, setBusy] = useState('');
  const [arm, setArm] = useState<string | null>(null);   // 「잇기 풀기」 두 번 눌러 확정
  useEffect(() => { if (!arm) return undefined; const t = window.setTimeout(() => setArm(null), 7000); return () => window.clearTimeout(t); }, [arm]);

  const load = useCallback(async () => {
    try { const r = await req<Desk>('GET', '/client-desk'); if (r.success) { setDesk(r.data); setErr(''); } else setErr(r.error || '불러오지 못했어요'); }
    catch (e) { setErr(e instanceof Error ? e.message : '불러오지 못했어요'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function act(key: string, method: string, path: string, body: unknown, done: string) {
    if (busy) return;
    setBusy(key);
    try {
      const r = await req<unknown>(method, path, body);
      if (r.success) { onToast(done, 'success'); await load(); } else onToast(r.error || '저장하지 못했어요', 'error');
    } catch (e) { onToast(e instanceof Error ? e.message : '저장하지 못했어요', 'error'); }
    finally { setBusy(''); }
  }

  if (err) return <div style={{ padding: 24, color: C.red, fontSize: T.lg }}>{err} <button type="button" style={btn()} onClick={load}>다시 불러오기</button></div>;
  if (!desk) return <div style={{ padding: 24, color: C.sub, fontSize: T.lg }}>불러오는 중…</div>;

  const soon = desk.deadlines.filter(d => d.status === 'open' && d.dday != null && d.dday <= 30).length;
  const open = desk.requests.filter(r => r.task_status && r.task_status !== '완료' && r.task_status !== '중단').length;
  const fits = desk.fits || [];
  const TABS: [Tab, string, number | null][] = [['deadlines', '기한', desk.deadlines.length], ['fits', '정보 채우기', fits.length], ['requests', '처리 요청', desk.requests.length], ['asks', '자료 요청', null], ['fps', '이어진 팀장님', desk.fps.length], ['rules', '규칙표 · 아침 문자', null]];

  return (
    <div style={{ height: '100%', overflowY: 'auto', overscrollBehavior: 'contain' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: narrow ? 12 : 20, maxWidth: 1200, margin: '0 auto', boxSizing: 'border-box', width: '100%' }}>
        <div style={{ ...card, padding: '16px 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ fontSize: T.xl, fontWeight: 800 }}>거래처 기한</div>
            <div style={{ fontSize: T.sm, color: C.sub, marginTop: 2 }}>fphub 거래처 관리와 같은 데이터 · {md(desk.today)}</div>
          </div>
          <Stat label="30일 안 기한" n={soon} tone={soon ? '#B42318' : C.sub} />
          <Stat label="진행 중 요청" n={open} tone={NAVY} />
          <Stat label="이어진 팀장님" n={desk.fps.length} tone={C.sub} />
          {fits.length > 0 && <button type="button" onClick={() => setTab('fits')} style={{ ...linkBtn, padding: 0 }}><Stat label="정보 채울 거래처" n={fits.length} tone="#B42318" /></button>}
        </div>

        <div role="tablist" aria-label="보기" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {TABS.map(([k, label, n]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
              style={{ height: 36, padding: '0 14px', borderRadius: 18, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: T.md, fontWeight: tab === k ? 800 : 600, background: tab === k ? NAVY : '#E9EDF2', color: tab === k ? '#fff' : C.text, whiteSpace: 'nowrap' }}>
              {label}{n != null ? ` ${n}` : ''}
            </button>
          ))}
        </div>

        {tab === 'deadlines' && (
          <div style={card}>
            <Head cols={narrow ? '64px minmax(0,1fr)' : '72px minmax(0,1.4fr) minmax(0,1.2fr) 96px 120px 88px'} labels={narrow ? ['', '거래처 · 기한'] : ['남은 날', '거래처', '기한', '기한일', '팀장님', '']} />
            {desk.deadlines.length === 0 && <Empty text="아직 등록된 기한이 없어요. 팀장님이 거래처를 등록하면 인증 만료일이 자동으로 들어와요." />}
            {desk.deadlines.map(d => {
              const [bg, fg] = ddayTone(d.dday);
              return (
                <Row key={d.id} cols={narrow ? '64px minmax(0,1fr)' : '72px minmax(0,1.4fr) minmax(0,1.2fr) 96px 120px 88px'}>
                  <span style={{ background: bg, color: fg, borderRadius: 999, fontSize: T.sm, fontWeight: 800, textAlign: 'center', padding: '4px 0', fontVariantNumeric: 'tabular-nums' }}>{ddayTxt(d.dday)}</span>
                  {narrow ? (
                    <button type="button" onClick={() => onOpenCompany(d.company_id)} style={{ ...linkBtn, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'stretch' }}>
                      <b style={ell}>{d.company_name}</b><span style={{ ...ell, color: C.sub, fontSize: T.sm }}>{d.title} · {md(d.due_date)}{d.status === 'requested' ? ' · 요청함' : ''}</span>
                    </button>
                  ) : (
                    <>
                      <button type="button" onClick={() => onOpenCompany(d.company_id)} style={{ ...linkBtn, ...ell, fontWeight: 700 }} title={d.company_name}>{d.company_name}</button>
                      <span style={ell} title={d.title}>{d.title}<span style={{ color: MUTED, fontSize: T.sm }}> · {SRC[d.source] || d.source}</span></span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{md(d.due_date)}</span>
                      <span style={{ ...ell, color: C.sub }} title={d.fps.join(', ')}>{d.fps.join(', ') || '-'}</span>
                      <span style={{ fontSize: T.sm, fontWeight: 700, color: d.status === 'requested' ? '#047857' : MUTED }}>{d.status === 'requested' ? '요청함' : ''}</span>
                    </>
                  )}
                </Row>
              );
            })}
          </div>
        )}

        {tab === 'fits' && <FitsPanel fits={fits} narrow={narrow} busy={busy} onOpenCompany={onOpenCompany}
          onFilled={(f) => act(`fit-${f.company_id}`, 'PATCH', `/client-desk/fits/${f.company_id}`, { filled: true }, `${f.company_name} 정보를 채웠어요`)} />}

        {tab === 'asks' && <ClientAsksPanel canEdit={desk.can_edit} narrow={narrow} onOpenCompany={onOpenCompany} onToast={onToast} />}
        {tab === 'requests' && (
          <div style={card}>
            <Head cols={narrow ? 'minmax(0,1fr) 80px' : '84px minmax(0,1.2fr) 92px minmax(0,1.6fr) 120px 80px'} labels={narrow ? ['요청', '상태'] : ['보낸 날', '거래처', '종류', '만든 업무 · 전한 말', '팀장님', '상태']} />
            {desk.requests.length === 0 && <Empty text="아직 처리 요청이 없어요. 요청이 오면 정식 업무와 오재인 님 일일 업무가 같이 생겨요." />}
            {desk.requests.map(r => {
              const st = r.task_status === '완료' ? ['끝남', '#4B5563', '#F3F4F6'] : r.task_status === '중단' ? ['멈춤', '#4B5563', '#F3F4F6'] : r.task_status ? ['진행 중', '#047857', '#D1FAE5'] : ['업무 없음', '#B42318', '#FDECEC'];
              const what = [r.task_type, r.memo].filter(Boolean).join(' · ');
              return (
                <Row key={r.id} cols={narrow ? 'minmax(0,1fr) 80px' : '84px minmax(0,1.2fr) 92px minmax(0,1.6fr) 120px 80px'}>
                  {narrow ? (
                    <button type="button" onClick={() => onOpenCompany(r.company_id)} style={{ ...linkBtn, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'stretch' }}>
                      <b style={ell}>{r.company_name} · {r.kind_label}</b><span style={{ ...ell, color: C.sub, fontSize: T.sm }}>{md(r.created_at)} · {r.fp}</span>
                    </button>
                  ) : (
                    <>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{md(r.created_at)}</span>
                      <button type="button" onClick={() => onOpenCompany(r.company_id)} style={{ ...linkBtn, ...ell, fontWeight: 700 }} title={r.company_name}>{r.company_name}</button>
                      <span style={ell}>{r.kind_label}</span>
                      <span style={{ ...ell, color: C.sub }} title={what}>{what || '-'}</span>
                      <span style={{ ...ell, color: C.sub }} title={r.fp}>{r.fp}</span>
                    </>
                  )}
                  <span style={{ justifySelf: 'start', background: st[2], color: st[1], borderRadius: 999, padding: '3px 10px', fontSize: T.sm, fontWeight: 700, whiteSpace: 'nowrap' }}>{st[0]}</span>
                </Row>
              );
            })}
          </div>
        )}

        {tab === 'fps' && (
          <div style={card}>
            <Head cols={narrow ? 'minmax(0,1fr) auto' : 'minmax(0,1.2fr) minmax(0,1.2fr) 80px 104px 80px 120px 112px'} labels={narrow ? ['이름', ''] : ['이름', '소속', '휴대폰', '이은 날', '거래처', '역할', '']} />
            {desk.fps.length === 0 && <Empty text="아직 fphub 와 이어진 팀장님이 없어요. 팀장님이 fphub 거래처 관리에서 휴대폰 인증을 하면 여기에 보여요." />}
            {desk.fps.map(f => (
              <Row key={f.id} cols={narrow ? 'minmax(0,1fr) auto' : 'minmax(0,1.2fr) minmax(0,1.2fr) 80px 104px 80px 120px 112px'}>
                {narrow ? (
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}><b style={ell}>{f.name} {f.title}님</b><span style={{ ...ell, color: C.sub, fontSize: T.sm }}>{f.company || '-'} · 거래처 {f.clients}곳 · {md(f.linked_at)}{f.morning_off ? ' · 아침 문자 끔' : ''}</span></span>
                ) : (
                  <>
                    <b style={ell}>{f.name} {f.title}님{f.active ? '' : ' (사용 안 함)'}</b>
                    <span style={{ ...ell, color: C.sub }} title={f.company}>{f.company || '-'}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', color: C.sub }}>…{f.tail}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{md(f.linked_at)}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{f.clients}곳</span>
                    {desk.can_edit ? (
                      <select value={f.role} disabled={!!busy} onChange={e => act(`role-${f.id}`, 'PUT', `/client-desk/fps/${f.id}`, { role: e.target.value }, '역할을 바꿨어요')}
                        style={{ height: 32, borderRadius: 8, border: `1px solid ${C.borderDk}`, fontSize: T.md, padding: '0 8px', fontFamily: 'inherit', background: '#fff', color: C.text }}>
                        <option value="fp">팀장님</option><option value="tax">세무사</option>
                      </select>
                    ) : <span>{f.role === 'tax' ? '세무사' : '팀장님'}</span>}
                  </>
                )}
                {desk.can_edit ? (
                  <button type="button" disabled={!!busy} style={{ ...btn(arm === f.id ? 'pri' : 'line', !!busy), background: arm === f.id ? '#B42318' : '#fff', borderColor: arm === f.id ? '#B42318' : C.borderDk, color: arm === f.id ? '#fff' : C.text }}
                    onClick={() => { if (arm !== f.id) { setArm(f.id); return; } setArm(null); act(`unlink-${f.id}`, 'POST', `/client-desk/fps/${f.id}/unlink`, {}, `${f.name} ${f.title}님 잇기를 풀었어요`); }}>
                    {arm === f.id ? '정말 풀기' : '잇기 풀기'}
                  </button>
                ) : <span />}
              </Row>
            ))}
          </div>
        )}

        {tab === 'rules' && (
          <>
            <div style={{ ...card, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: T.lg, fontWeight: 800 }}>아침 묶음 문자</div>
              <div style={{ fontSize: T.md, color: C.sub, lineHeight: 1.6 }}>평일 아침 8시 40분쯤, 알릴 때가 된 기한과 새로 받을 수 있는 지원사업을 팀장님마다 한 통으로 묶어요. 보낼 것이 없으면 보내지 않아요.</div>
              {!desk.can_edit && <div style={{ fontSize: T.sm, color: C.sub }}>받는 사람은 지도사만 바꿀 수 있어요.</div>}
              <div role="radiogroup" aria-label="받는 사람" style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3, minmax(0,1fr))', gap: 8 }}>
                {(Object.keys(MODE) as Desk['morning']['mode'][]).map(k => {
                  const on = desk.morning.mode === k;
                  return (
                    <button key={k} type="button" role="radio" aria-checked={on} disabled={!desk.can_edit || !!busy}
                      onClick={() => {
                        if (on) return;
                        // 팀장님께 실제로 가는 쪽으로 바꿀 때는 두 번 눌러 확정(2026-10-24 디자인 검토)
                        if (k === 'fp' && arm !== 'mode-fp') { setArm('mode-fp'); return; }
                        setArm(null); act('mode', 'PUT', '/client-desk/setting', { mode: k }, `아침 문자: ${MODE[k][0]}`);
                      }}
                      style={{ textAlign: 'left', padding: 12, borderRadius: 10, cursor: desk.can_edit ? 'pointer' : 'not-allowed', fontFamily: 'inherit', background: on ? '#F1F5F9' : desk.can_edit ? '#fff' : '#F9FAFB', border: on ? `2px solid ${NAVY}` : arm === 'mode-fp' && k === 'fp' ? '2px solid #B42318' : `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <b style={{ fontSize: T.md, color: C.text }}>{MODE[k][0]}</b>
                      <span style={{ fontSize: T.sm, color: arm === 'mode-fp' && k === 'fp' ? '#B42318' : C.sub, lineHeight: 1.5, fontWeight: arm === 'mode-fp' && k === 'fp' ? 700 : 400 }}>
                        {arm === 'mode-fp' && k === 'fp' ? `내일 아침부터 팀장님 ${desk.fps.length}명께 보내요. 한 번 더 누르면 바뀌어요.` : MODE[k][1]}
                      </span>
                    </button>
                  );
                })}
              </div>
              {desk.morning.days.length > 0 && (
                <div style={{ fontSize: T.sm, color: C.sub, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {desk.morning.days.map(d => <span key={d.day} style={{ fontVariantNumeric: 'tabular-nums' }}>{md(d.day)} 보냄 {d.sent} · 보낼 것 없음 {d.empty}{d.failed ? ` · 실패 ${d.failed}` : ''}{d.skipped ? ` · 건너뜀 ${d.skipped}` : ''}{d.held ? ` · 소식 모아 둠 ${d.held}` : ''}</span>)}
                </div>
              )}
            </div>
            <div style={card}>
              <Head cols={narrow ? 'minmax(0,1fr)' : 'minmax(0,1.4fr) 80px minmax(0,1fr) 140px 90px minmax(0,1fr)'} labels={narrow ? ['규칙'] : ['기한 종류', '묶음', '계산', '며칠 전 알림', '사용', '검수']} />
              {desk.rules.map(r => <RuleRow key={r.key} r={r} narrow={narrow} canEdit={desk.can_edit} busy={!!busy} onSave={(body, msg) => act(`rule-${r.key}`, 'PUT', `/client-desk/rules/${r.key}`, body, msg)} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const linkBtn: CSSProperties = { border: 'none', background: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', color: C.text, fontSize: T.md, fontFamily: 'inherit' };

function Stat({ label, n, tone }: { label: string; n: number; tone: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 96 }}>
      <span style={{ fontSize: 22, fontWeight: 800, color: tone, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
      <span style={{ fontSize: T.sm, color: C.sub, whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}
function Head({ cols, labels }: { cols: string; labels: string[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, padding: '10px 16px', background: '#F8FAFC', borderBottom: `1px solid ${C.border}`, fontSize: T.sm, fontWeight: 700, color: C.sub }}>
      {labels.map((l, i) => <span key={i} style={ell}>{l}</span>)}
    </div>
  );
}
function Row({ cols, children }: { cols: string; children: ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, alignItems: 'center', padding: '10px 16px', minHeight: 48, borderBottom: `1px solid ${C.border}`, fontSize: T.md }}>{children}</div>;
}
function Empty({ text }: { text: string }) {
  return <div style={{ padding: '20px 16px', fontSize: T.md, color: C.sub, lineHeight: 1.6 }}>{text}</div>;
}

// ───────────── 정보 채우기(2026-10-26) ─────────────
const FIT_Q: [keyof Pick<Fit, 'own_product' | 'makes' | 'store'>, string][] = [['own_product', '자체 제품'], ['makes', '직접 생산'], ['store', '매장']];
const SAY: Record<Ans, string> = { yes: '예', no: '아니요', unknown: '모름', '': '답 안 함' };
/** 답 칩 — 예 남색 연한 바탕 · 아니요 회색 · 모름 · 답 안 함 점선(모두 같은 폭) */
function AnsChip({ label, v }: { label: string; v: Ans }) {
  const tone: CSSProperties = v === 'yes' ? { background: '#E8EEF6', color: NAVY, border: '1px solid #E8EEF6' } : v === 'no' ? { background: '#F3F4F6', color: '#4B5563', border: '1px solid #F3F4F6' } : { background: '#fff', color: MUTED, border: `1px dashed ${C.borderDk}` };
  return <span style={{ ...tone, display: 'inline-flex', justifyContent: 'center', width: 104, boxSizing: 'border-box', borderRadius: 6, padding: '3px 0', fontSize: T.sm, fontWeight: 700, whiteSpace: 'nowrap' }}>{label} {SAY[v]}</span>;
}
function FitsPanel({ fits, narrow, busy, onOpenCompany, onFilled }: { fits: Fit[]; narrow: boolean; busy: string; onOpenCompany: (id: string) => void; onFilled: (f: Fit) => void }) {
  const cols = narrow ? 'minmax(0,1fr) auto' : '84px minmax(0,1.2fr) minmax(0,0.9fr) 336px 76px 96px';
  return (
    <div style={card}>
      <div style={{ padding: '12px 16px', fontSize: T.md, color: C.sub, lineHeight: 1.6, borderBottom: `1px solid ${C.border}` }}>
        팀장님이 거래처 문답에 답했어요. 「예」 답은 관심 분류 · 기업 유형에 이미 넣었어요 — 업체를 열어 지역 · 업종 · 규모 · 매출 · 설립일 같은 나머지 매칭 정보를 채운 뒤 「채웠어요」를 눌러 주세요.
      </div>
      <Head cols={cols} labels={narrow ? ['거래처 · 팀장님 답', ''] : ['받은 날', '거래처', '팀장님', '팀장님 답', '매칭 정보', '']} />
      {fits.length === 0 && <Empty text="팀장님이 문답에 답하면 여기 보여요." />}
      {fits.map((f) => {
        const p = matchProgress(matchFieldsFromCompany(f.company));
        const chips = (
          <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{FIT_Q.map(([k, l]) => <AnsChip key={k} label={l} v={f[k]} />)}</span>
            {f.conflict && <span style={{ fontSize: T.sm, color: '#B42318', fontWeight: 700 }}>매장운영 「아님」으로 두신 업체예요</span>}
          </span>
        );
        const done = <button type="button" disabled={!!busy} style={{ ...btn('pri', !!busy), width: 96 }} onClick={() => onFilled(f)}>채웠어요</button>;
        const fill = <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: p.filled < p.total ? '#B42318' : '#047857' }} title={p.missing.length ? `빈 칸: ${p.missing.join(', ')}` : ''}>{p.filled}/{p.total}</span>;
        return narrow ? (
          <Row key={f.company_id} cols={cols}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <button type="button" onClick={() => onOpenCompany(f.company_id)} style={{ ...linkBtn, ...ell, fontWeight: 700 }}>{f.company_name}</button>
              <span style={{ ...ell, color: C.sub, fontSize: T.sm }}>{md(f.answered_at)} · {f.fp_name} · 매칭 정보 {fill}</span>
              {chips}
            </span>
            {done}
          </Row>
        ) : (
          <Row key={f.company_id} cols={cols}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{md(f.answered_at)}</span>
            <button type="button" onClick={() => onOpenCompany(f.company_id)} style={{ ...linkBtn, ...ell, fontWeight: 700 }} title={f.company_name}>{f.company_name}</button>
            <span style={{ ...ell, color: C.sub }} title={f.fp_name}>{f.fp_name}</span>
            {chips}
            {fill}
            {done}
          </Row>
        );
      })}
    </div>
  );
}

const KIND: Record<string, string> = { lookup: '조회한 만료일', start: '시작일 + 기간', due: '기한일 그대로' };
const UNIT: Record<string, string> = { year: '년', month: '개월', day: '일' };

function RuleRow({ r, narrow, canEdit, busy, onSave }: { r: Rule; narrow: boolean; canEdit: boolean; busy: boolean; onSave: (body: Record<string, unknown>, msg: string) => void }) {
  const [days, setDays] = useState(r.alert_days.join(', '));
  const [rev, setRev] = useState(r.reviewed_by);
  useEffect(() => { setDays(r.alert_days.join(', ')); setRev(r.reviewed_by); }, [r.alert_days, r.reviewed_by]);
  const calc = r.base_kind === 'start' && r.period ? `시작일 + ${r.period}${UNIT[r.unit || ''] || ''}` : KIND[r.base_kind] || r.base_kind;
  const daysChanged = days.replace(/\s/g, '') !== r.alert_days.join(',');
  const saveDays = () => { if (daysChanged) onSave({ alert_days: days }, `${r.name} 알림 일수를 바꿨어요`); };
  const saveRev = () => { if (rev !== r.reviewed_by) onSave({ reviewed_by: rev }, `${r.name} 검수를 적었어요`); };
  const input: CSSProperties = { height: 32, borderRadius: 8, border: `1px solid ${C.borderDk}`, padding: '0 8px', fontSize: T.md, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', minWidth: 0 };
  const cells = (
    <>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}><b style={ell} title={r.name}>{r.name}</b>{narrow && <span style={{ ...ell, fontSize: T.sm, color: C.sub }}>{r.grp} · {calc}</span>}</span>
      {!narrow && <span style={{ color: C.sub }}>{r.grp}</span>}
      {!narrow && <span style={{ ...ell, color: C.sub }}>{calc}</span>}
      {/* 입력칸은 Enter · 칸을 벗어날 때 저장(저장 단추가 나타나 칸 폭이 바뀌지 않게) */}
      <span style={{ minWidth: 0 }}>
        {canEdit
          ? <input aria-label="며칠 전 알림" value={days} disabled={busy} onChange={e => setDays(e.target.value)} onBlur={saveDays} onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} style={input} />
          : <span>{r.alert_days.join(', ')}</span>}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {canEdit ? (
          <button type="button" disabled={busy} role="switch" aria-checked={r.active} aria-label={`${r.name} 사용`} onClick={() => onSave({ active: !r.active }, `${r.name} ${r.active ? '끔' : '켬'}`)}
            style={{ width: 36, height: 20, borderRadius: 10, border: 'none', padding: 2, cursor: busy ? 'not-allowed' : 'pointer', background: r.active ? '#047857' : '#D1D5DB', display: 'flex', justifyContent: r.active ? 'flex-end' : 'flex-start', flexShrink: 0 }}>
            <i style={{ width: 16, height: 16, borderRadius: 8, background: '#fff', display: 'block' }} />
          </button>
        ) : null}
        <span style={{ fontSize: T.sm, color: C.sub }}>{r.active ? '사용' : '안 씀'}</span>
      </span>
      <span style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 64px', gap: 6, alignItems: 'center', minWidth: 0 }}>
        {canEdit
          ? <input aria-label="검수한 분" value={rev} disabled={busy} placeholder="검수한 분" onChange={e => setRev(e.target.value)} onBlur={saveRev} onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} style={input} />
          : <span style={ell}>{r.reviewed_by || '-'}</span>}
        <span style={{ fontSize: T.sm, color: MUTED, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.reviewed_at ? md(r.reviewed_at) : ''}</span>
      </span>
    </>
  );
  return narrow
    ? <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${C.border}`, fontSize: T.md }}>{cells}</div>
    : <Row cols="minmax(0,1.4fr) 80px minmax(0,1fr) 140px 90px minmax(0,1fr)">{cells}</Row>;
}
